import { useEffect, useRef, useState } from "react";
import { Button } from "antd";
import { useTranslation } from "react-i18next";
import { registerSW } from "virtual:pwa-register";
import {
  BOTTOM_TAB_BAR_HEIGHT,
  FONT,
  RADIUS,
  SAFE_AREA,
  TEXT,
  TOUCH_MIN,
} from "../config/uiTokens";

/** 長時間開著不關的 PWA 內部切頁不算導航，瀏覽器不會自己去問有沒有新版，需定期主動檢查。 */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

// ponytail: 模組層變數而非 Context —— UpdatePrompt 全站只掛一份，供 /ui-demo 預覽用，不值得為此開 Provider
let forceShow = null;

/** 供 /ui-demo 預覽橫幅版面用；不會真的去檢查或安裝新版。 */
export function simulateUpdateAvailable() {
  forceShow?.();
}

/**
 * Service Worker 註冊 + 發版後的更新提示。
 *
 * 不使用 autoUpdate（抓到新版就自動 reload）：本專案有即時影像串流，
 * 中途強制重新載入會直接把畫面打斷，改為跳橫幅讓使用者自己決定何時更新。
 */
export default function UpdatePrompt() {
  const { t } = useTranslation();
  const [needRefresh, setNeedRefresh] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [simulated, setSimulated] = useState(false);
  const updateSWRef = useRef(null);

  useEffect(() => {
    forceShow = () => {
      setSimulated(true);
      setNeedRefresh(true);
    };
    return () => {
      forceShow = null;
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    let timer = null;
    updateSWRef.current = registerSW({
      immediate: true,
      onNeedRefresh: () => {
        // 清掉預覽狀態：真的有新版時必須走 updateSW()，用 reload 換不掉還在 waiting 的 SW
        setSimulated(false);
        setNeedRefresh(true);
      },
      onRegisteredSW: (_swUrl, registration) => {
        if (!registration) return;
        timer = setInterval(() => registration.update(), UPDATE_CHECK_INTERVAL_MS);
      },
    });

    return () => {
      if (timer) clearInterval(timer);
    };
  }, []);

  const handleUpdate = async () => {
    setUpdating(true);
    // 預覽時底下沒有 waiting 的 SW，updateSW 不會有任何反應（按鈕會卡在 loading）；
    // 直接重載，結果與真實更新一致
    if (simulated) {
      window.location.reload();
      return;
    }
    // updateSW(true) 會通知 waiting 的 SW 立刻接手，接手後自動重新載入頁面
    await updateSWRef.current?.(true);
  };

  if (!needRefresh) return null;

  return (
    <div
      role="alert"
      style={{
        position: "fixed",
        left: 12,
        right: 12,
        // 墊在底部 Tab Bar 之上，避免蓋住導覽（未進 MainLayout 的頁面會略高於底部，無妨）
        bottom: `calc(${BOTTOM_TAB_BAR_HEIGHT}px + ${SAFE_AREA.bottom} + 12px)`,
        zIndex: 600,
        padding: 16,
        background: "#fff",
        borderRadius: RADIUS.md,
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.18)",
      }}
    >
      <div style={{ fontSize: FONT.subtitle, fontWeight: 600, color: TEXT.primary }}>
        {t("appUpdate.title")}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <Button
          size="large"
          disabled={updating}
          onClick={() => setNeedRefresh(false)}
          style={{ flex: 1, minHeight: TOUCH_MIN }}
        >
          {t("appUpdate.later")}
        </Button>
        {/* 主要操作給兩倍寬度，年長使用者較好按中 */}
        <Button
          type="primary"
          size="large"
          loading={updating}
          onClick={handleUpdate}
          style={{ flex: 2, minHeight: TOUCH_MIN }}
        >
          {updating ? t("appUpdate.updating") : t("appUpdate.action")}
        </Button>
      </div>
    </div>
  );
}
